import { AxiosRequestConfig } from 'axios';
import client from '../axios';
import { BulkDocumentResponse, IUploadOnboardingDocuments } from './types';

export interface IDocument {
  document_type_id: number;
  id: number;
  name: string;
}

export interface IUploadDocumentsResponse {
  documents: IDocument[];
}

interface DocumentMetadata {
  number: string;
  [key: string]: string;
}

interface IOnboardingDocument {
  type: {
    id: number;
    name: string;
    description: string;
  };
  location: string;
  version: string;
  metaData?: Record<string, DocumentMetadata>;
}

interface IStakeholderAssignDocument {
  metadata: Record<string, string>;
  enterpriseId: number;
  documentId: number;
}

export interface IOnboardingDocuments {
  id: number;
  document: IOnboardingDocument;
}

const upload = (
  data: any,
  config?: AxiosRequestConfig
): Promise<IUploadDocumentsResponse> =>
  client.post('documents/upload/', data, {
    isAuthRedirect: false,
    headers: {
      'Content-Type': 'multipart/form-data',
    },
    ...config,
  });

const getFiles = async (): Promise<BulkDocumentResponse[]> =>
  client.get('documents/');

const uploadOnboardingDocument = async (
  enterpriseId: number,
  data: IUploadOnboardingDocuments
) => {
  const files: Record<string, any> = {};
  if (Array.isArray(data.documents)) {
    data.documents.forEach((file, index) => {
      files[`file-${index}`] = file;
    });
  } else {
    files['file-0'] = data.documents;
  }

  data.documents = undefined;

  const response = await upload({ ...data, ...files });
  const documentIds = response.documents.map((document) => document.id);
  return client.post(`documents/enterprise/${enterpriseId}/onboarding/`, {
    documentIds,
  });
};

const asignStakeholderDocuments = async ({
  metadata,
  enterpriseId,
  documentId,
}: IStakeholderAssignDocument) =>
  client.put(
    `documents/enterprise/${enterpriseId}/onboarding/${documentId}/`,
    metadata
  );

const uploadStakeholderOnboardingDocument = async (
  enterpriseId: number,
  data: IUploadOnboardingDocuments,
  savedMetaData?: { number: string }
): Promise<IOnboardingDocuments> => {
  const files: Record<string, any> = {};
  if (Array.isArray(data.documents)) {
    data.documents.forEach((file, index) => {
      files[`file-${index}`] = file;
    });
  } else {
    files['file-0'] = data.documents;
  }

  data.documents = undefined;

  const response = await upload({ ...data, ...files });
  const documentIds = response.documents.map((document) => document.id);

  return client.post(`documents/enterprise/${enterpriseId}/onboarding/`, {
    documentIds,
    metadata: savedMetaData ? { number: savedMetaData.number } : undefined,
  });
};

const getOnboardingDocuments = async (
  enterpriseId?: number
): Promise<IOnboardingDocuments[]> =>
  client.get(`documents/enterprise/${enterpriseId}/onboarding/`);

const deleteOnboardingDocuments = async (
  enterpriseId?: number,
  documentId?: number
): Promise<IOnboardingDocuments[]> =>
  client.delete(
    `documents/enterprise/${enterpriseId}/onboarding/${documentId}/`
  );

const documentsSvc = {
  upload,
  getFiles,
  uploadOnboardingDocument,
  getOnboardingDocuments,
  deleteOnboardingDocuments,
  uploadStakeholderOnboardingDocument,
  asignStakeholderDocuments,
};

export default documentsSvc;
